import { getModule } from '@jwp/ott-common/src/modules/container';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import type { DeleteAccountFormData } from '@jwp/ott-common/types/account';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useLocation, useNavigate } from 'react-router';
import { object, string } from 'yup';
import { FormValidationError } from '@jwp/ott-common/src/errors/FormValidationError';
import { cognitoLogout, customDeleteAccount } from '@jwp/ott-common/src/services/integrations/CustomAccountService';

import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import CheckoutController from '@jwp/ott-common/src/controllers/CheckoutController';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';

import { useTheme } from '../../../../../ThemeContext';
import { useAriaAnnouncer } from '../../containers/AnnouncementProvider/AnnoucementProvider';
import { modalURLFromLocation } from '../../utils/location';
import Button from '../Button/Button';
import PasswordField from '../PasswordField/PasswordField';
import useQueryParam from '../../hooks/useQueryParam';
import FormFeedback from '../FormFeedback/FormFeedback';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';

import styles from './DeleteAccountModal.module.scss';

const DeleteAccountModal = () => {
  const accountController = getModule(AccountController);
  const checkoutController = getModule(CheckoutController);
  const { getAccountInfo } = useAccountStore.getState();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { isCognitoEnabled, isCustomApiEnabled, isCleeng } = useAppConfig();

  const safeGetAccountInfo = () => {
    try {
      return getAccountInfo();
    } catch {
      return null;
    }
  };

  const accountInfo = safeGetAccountInfo();

  const { t } = useTranslation(['user', 'common']);
  const { universalStyles } = useTheme();

  const announce = useAriaAnnouncer();
  const u = useQueryParam('u');

  const [enteredPassword, setEnteredPassword] = useState<string>('');

  const navigate = useNavigate();
  const location = useLocation();

  const {
    handleSubmit,
    handleChange,
    values,
    errors,
    setErrors,
    reset: resetForm,
  } = useForm<DeleteAccountFormData>({
    initialValues: { password: '' },
    validationSchema: object().shape({ password: string().required(t('user:login.field_required', { field: t('user:password') })) }),
    onSubmit: (values) => {
      setEnteredPassword(values.password);
      navigate(modalURLFromLocation(location, 'delete-account-confirmation'), { replace: true });
    },
  });

  const deleteAccount = useMutation(accountController.deleteAccountData, {
    onSuccess: async () => {
      await accountController.logout();
      window.location.href = '/';
      announce(t('account.delete_account.success'), 'success');
    },
    onError: (error: unknown) => {
      if (error instanceof FormValidationError) {
        setErrors(error.errors);
        navigate(modalURLFromLocation(location, 'delete-account'), { replace: true });
      }
      setEnteredPassword('');
    },
  });

  useEffect(() => {
    // when the user navigates directly to the confirmation step
    if (u === 'delete-account-confirmation' && !enteredPassword && !isCleeng) {
      navigate(modalURLFromLocation(location, 'delete-account'), { replace: true });
    }
  }, [location, u, navigate, enteredPassword, deleteAccount, resetForm]);

  const handleCancel = useCallback(() => {
    navigate(modalURLFromLocation(location, null), { replace: true });
  }, [location, navigate]);

  const handleCleengAccountDelete = async () => {

    if (accountInfo?.customerId) {
      setLoading(true);
      try {
        const response = await checkoutController.deleteAccountByCustomerId(accountInfo?.customerId);
        if (response.data) {
          await accountController.logout();
          window.location.href = '/';
        } else {
          setError(response.error.message);
        }
      } catch (error) {
        setError((error as any).error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteAccount = async () => {
    const response = await customDeleteAccount();

    if (response) {
      if (isCognitoEnabled) {
        cognitoLogout();
      } else {
        await accountController.logout();
      }
      window.location.href = '/';
      announce(t('account.delete_account.success'), 'success');
    } else {
      setEnteredPassword('');
    }
  };

  // step 2: delete account confirmation
  if (isCustomApiEnabled || (!isCustomApiEnabled && enteredPassword) || isCleeng) {
    return (
      <div>
        {loading && <LoadingOverlay transparentBackground inline />}
        <h2 className={styles.headingConfirmBox}>{t('account.delete_account.title')}</h2>
        {/* <div className={styles.disclaimer}> */}
        {error && <p className={styles.error}>{error}</p>}
        {/* <p>{t('account.delete_account.modal.text_revoked_access')}</p> */}
        {/* <div className={styles.warningBox}>
            <p>{t('account.delete_account.modal.warning')}</p>
          </div> */}
        {/* <p>
            {t('account.delete_account.modal.text_cant_be_undone')} <br />
            {t('account.delete_account.modal.text_contacts')}
          </p> */}
        {/* </div> */}
        <div>
          <p className={styles.paragraphConfirmBox} style={universalStyles.primaryTextColor}>
            {t('common:delete_confirm_content')}
          </p>
        </div>
        <div className={styles.buttons}>
          <Button
            disabled={deleteAccount.isLoading}
            size="exlarge"
            variant="signoutYes"
            label={t('common:yes')}
            onClick={() => {
              isCleeng ? handleCleengAccountDelete() : isCognitoEnabled ? handleDeleteAccount() : deleteAccount.mutate(enteredPassword);
            }}
            fullWidth
            style={universalStyles.ternaryButton}
            textStyle={universalStyles.signInButtonText}
          />
          <Button
            disabled={deleteAccount.isLoading}
            color="primary"
            size="exlarge"
            variant="contained"
            label={t('common:no')}
            onClick={handleCancel}
            fullWidth
            style={universalStyles.signUpButton}
            textStyle={universalStyles.signUpButtonText}
          />
        </div>
      </div>
    );
  }

  // step 1: enter your password
  return (
    <form onSubmit={handleSubmit} noValidate>
      <h2 className={styles.heading} style={universalStyles.primaryTextColor}>
        {t('user:account.delete_account.title')}
      </h2>
      <p className={styles.paragraph} style={universalStyles.inActiveColor}>
        {t('user:account.delete_account.modal.description')}
      </p>
      {errors.form && <FormFeedback variant="error">{errors.form}</FormFeedback>}
      <PasswordField
        value={values.password}
        onChange={handleChange}
        label={t('user:account.password')}
        placeholder={t('user:account.delete_account.modal.placeholder')}
        error={!!errors.password || !!errors.form}
        helperText={errors.password}
        name="password"
        showHelperText={false}
      />
      <div className={styles.buttons}>
        <Button
          type="submit"
          color="primary"
          size="exlarge"
          variant="contained"
          label={t('user:account.delete_account.title')}
          fullWidth
          style={universalStyles.signUpButton}
          textStyle={universalStyles.signUpButtonText}
        />
      </div>
    </form>
  );
};

export default DeleteAccountModal;
